import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';
import { requestPhoneCode } from '@realadvisor/auth-client';
import { useCaptcha } from '@realadvisor/hooks';

import { reCaptchaKeyV2, reCaptchaKeyV3 } from '@legacy/config';
import { type Translate, useLocale } from '@legacy/hooks/locale';
import { collectMarketingMetadata } from '@legacy/routes/Login/LoginPhoneForm';
import { formatValidPhoneNumberE164 } from '@modules/_shared/utils/phone';

const isCaptchaError = (message: string | undefined | null) =>
  message?.toLowerCase().includes('captcha') ?? false;

// Must stay above the auth service's minimum interval between OTP requests
// (20s, auth/otp-throttle.ts) — a shorter cooldown lets the button fire a
// request auth is guaranteed to refuse with 429.
//
// 60s while HighConnexion is paused from routing (2026-08-14, bill
// negotiation): CRM login OTPs are `auth_code_crm`, which is Twilio-eligible,
// so on the paused two-provider stack the first resend starts on Twilio — the
// expensive route. A premature resend then costs money, exactly as on the
// valuation funnel (see valuation-studio's otp-resend). Restore 30s when HC
// returns to the stack.
const RESEND_COOLDOWN_SECONDS = 60;

export const ResendCodeButton = ({
  phone,
  tenantId,
  onCancel,
}: {
  phone: string;
  tenantId: string | null;
  onCancel: () => void;
}) => {
  const { t } = useLocale();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [captchaSolved, setCaptchaSolved] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [didNotReceiveTheCode, setDidNotReceiveTheCode] = useState(false);
  const [
    numberOfTimesTheCodeWasRequested,
    setNumberOfTimesTheCodeWasRequested,
  ] = useState(0);

  const captcha = useCaptcha({
    reCaptchaKeyV2,
    reCaptchaKeyV3,
    onSolved: () => setCaptchaSolved(true),
  });

  const setNextTimer = useCallback((time: number) => {
    if (timerRef.current != null) {
      clearInterval(timerRef.current);
    }

    setTimeLeft(time);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 0 && timerRef.current != null) {
          clearInterval(timerRef.current);
          return 0;
        }

        return t - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    // This component mounts right after the initial code was requested, so
    // the countdown starts immediately: without it the resend button was
    // enabled at 0s and the first resend hit the auth service inside its
    // minimum interval.
    setNextTimer(RESEND_COOLDOWN_SECONDS);
    // Ensure timer is cleared when component unmounts
    return () => {
      if (timerRef.current != null) {
        clearInterval(timerRef.current);
      }
    };
  }, [setNextTimer]);

  const handleError = useCallback(
    (message: string | null | undefined, t: Translate) => {
      if (isCaptchaError(message)) {
        captcha.verifyUser();
        setCaptchaSolved(false);
      } else if (message === 'TOO_MANY_REQUESTS') {
        // Auth's rate-limit refusal is a machine token — don't show it raw.
        setError(t('Please wait a moment before requesting another code.'));
      } else {
        setError(message ?? t('Failed to request code'));
      }
    },
    [captcha],
  );

  const handleRetry = useCallback(() => {
    if (numberOfTimesTheCodeWasRequested >= 3) {
      return;
    }

    const normalizedNumber = formatValidPhoneNumberE164(phone);
    if (normalizedNumber == null) {
      return;
    }

    setError(null);
    setIsResending(true);

    captcha.requestToken(async recaptchaToken => {
      try {
        const response = await requestPhoneCode({
          number: normalizedNumber,
          tenantId,
          marketingMetadata: collectMarketingMetadata(),
          recaptchaToken,
        });

        if (response.ok) {
          const json = await response.json();

          if (json.hint != null) {
            alert(json.hint);
          }
        } else {
          const json = await response.json();
          handleError(json.error, t);
        }

        if (numberOfTimesTheCodeWasRequested + 1 < 3) {
          setNextTimer(RESEND_COOLDOWN_SECONDS);
        }

        setNumberOfTimesTheCodeWasRequested(
          numberOfTimesTheCodeWasRequested + 1,
        );
      } catch (err) {
        handleError(err instanceof Error ? err.message : null, t);
      } finally {
        setIsResending(false);
      }
    });
  }, [
    numberOfTimesTheCodeWasRequested,
    setNextTimer,
    captcha,
    phone,
    handleError,
    t,
    tenantId,
  ]);

  return (
    <Stack
      sx={{
        alignItems: 'center',
        my: 1,
      }}
    >
      {didNotReceiveTheCode ? (
        <>
          <Typography variant="body2">
            {t('Please verify your phone number:')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
            }}
          >
            {phone}
          </Typography>

          <Button variant="text" size="small" onClick={onCancel}>
            {t('Edit phone number')}
          </Button>

          <Box sx={{ mt: 1 }}>{captcha.element}</Box>

          <Button
            variant="contained"
            color="primary"
            loading={isResending}
            onClick={handleRetry}
            sx={{ mt: 1 }}
            disabled={
              !captchaSolved ||
              timeLeft !== 0 ||
              numberOfTimesTheCodeWasRequested >= 3
            }
          >
            {timeLeft !== 0
              ? t('{{seconds}}s before retrying', {
                  seconds: timeLeft.toString(),
                })
              : t('Re-send the sms code')}
          </Button>
          {error != null && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, textAlign: 'center' }}
            >
              {error}
            </Typography>
          )}
          {numberOfTimesTheCodeWasRequested >= 3 && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, textAlign: 'center' }}
            >
              {t(
                'You have reached the maximum number of attempts, if you still have not received your code, please let us know.',
              )}
            </Typography>
          )}
        </>
      ) : (
        <Button
          variant="text"
          onClick={() => {
            // Delay to ensure captcha.element is rendered before verifyUser
            setTimeout(() => captcha.verifyUser(), 0);
            setDidNotReceiveTheCode(true);
          }}
        >
          {t('I did not receive my code')}
        </Button>
      )}
    </Stack>
  );
};
